import { For, Show, createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import './ContextMenu.css';

const FOCUSABLE = 'button.v2-context-menu__item:not([disabled])';

export default function ContextMenu(props) {
  let menuRef;
  let previousFocus;
  const [pos, setPos] = createSignal({ x: props.x || 0, y: props.y || 0 });

  function enabledItems() {
    return Array.from(menuRef?.querySelectorAll(FOCUSABLE) || []);
  }

  function place() {
    if (!menuRef || typeof window === 'undefined') return;
    const rect = menuRef.getBoundingClientRect();
    const pad = 8;
    const x = Number.isFinite(props.x) ? props.x : 0;
    const y = Number.isFinite(props.y) ? props.y : 0;
    const clampedX = Math.min(Math.max(pad, x), Math.max(pad, window.innerWidth - rect.width - pad));
    const clampedY = Math.min(Math.max(pad, y), Math.max(pad, window.innerHeight - rect.height - pad));
    setPos({ x: clampedX, y: clampedY });
  }

  function close() {
    props.onClose?.();
  }

  function choose(item) {
    if (!item || item.disabled) return;
    close();
    item.onSelect?.();
  }

  function handleKey(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    const items = enabledItems();
    if (!items.length) return;
    const current = document.activeElement;
    const idx = Math.max(0, items.indexOf(current));
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(idx + 1) % items.length].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1].focus();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      items[e.shiftKey ? (idx - 1 + items.length) % items.length : (idx + 1) % items.length].focus();
    }
  }

  function handleDocPointerDown(e) {
    if (menuRef?.contains(e.target)) return;
    close();
  }

  createEffect(() => {
    if (!props.open) return;
    const frame = requestAnimationFrame(() => place());
    return () => cancelAnimationFrame(frame);
  });

  onMount(() => {
    previousFocus = document.activeElement;
    document.addEventListener('pointerdown', handleDocPointerDown, true);
    document.addEventListener('keydown', handleKey, true);
    window.addEventListener('resize', place);
    requestAnimationFrame(() => {
      place();
      enabledItems()[0]?.focus();
    });
  });
  onCleanup(() => {
    document.removeEventListener('pointerdown', handleDocPointerDown, true);
    document.removeEventListener('keydown', handleKey, true);
    window.removeEventListener('resize', place);
    if (previousFocus?.isConnected) previousFocus.focus();
  });

  return (
    <Show when={props.open}>
      <Portal>
        <div
          ref={el => menuRef = el}
          class="v2-context-menu"
          role="menu"
          aria-label={props.label || 'Context menu'}
          style={{ left: `${pos().x}px`, top: `${pos().y}px` }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <For each={props.items || []}>{(item) => (
            <Show when={!item.separator} fallback={<div class="v2-context-menu__sep" role="separator" />}>
              <button
                type="button"
                class="v2-context-menu__item"
                classList={{
                  'is-danger': !!item.danger,
                  'is-themed': item.iconTone === 'theme',
                }}
                role="menuitem"
                disabled={!!item.disabled}
                onClick={() => choose(item)}
              >
                <span class="v2-context-menu__icon" aria-hidden="true">{item.icon || '•'}</span>
                <span class="v2-context-menu__label">{item.label}</span>
                <Show when={item.shortcut}>
                  <span class="v2-context-menu__shortcut">{item.shortcut}</span>
                </Show>
              </button>
            </Show>
          )}</For>
        </div>
      </Portal>
    </Show>
  );
}